import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ScreenTitle from '../../common/components/screenTitle/ScreenTitle';
import ScreenContainer from '../../common/components/screenContainer/ScreenContainer';
import { CompactPageLayout } from 'common/components/compactPageLayout';
import Box from 'common/components/box/Box';
import Tabs, { TabItem } from 'common/components/tabs/Tabs';
import styles from './VehicleScreen.module.scss';
import CarDocumentTab from './components/carDocumentTab/CarDocumentTab';
import CarContractTab from './components/carContractTab/CarContractTab';
import CarDashboardTab from './components/carDashboardTab/CarDashboardTab';
import { useHistory, useParams } from 'react-router-dom';
import CarForm from './components/carForm/CarForm'
import Logger from 'common/services/Logger';
import { DATE_FORMAT_DEFAUT, DefaultAssetImage, DEFAULT_PAGINATION_ITEMS_PER_PAGE, FUELS, LOGGER_LOG_TYPE, SEGMENT_FEATURES } from 'Config';
import { useToasts } from 'react-toast-notifications';
import VehiclesService from 'api/vehicles/VehiclesService';
import { VehicleViewModel } from 'api/vehicles/models/VehicleViewModel';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import { Col, Row } from 'react-flexbox-grid';
import CarChargeTab from './components/carChargeTab/CarChargeTab';
import CarMaintenanceTab from './components/carMaintenanceTab/CarMaintenanceTab';
import CarVerificationTab from './components/carVerificationTab/CarVerificationTab';
import Loading from 'common/services/Loading';
import CarAccidentTab from './components/carAccidentTab/CarAccidentTab';
import DateFormat from 'common/components/dateFormat/dateFormat';
import CarGpsTab from './components/carGpsTab/CarGpsTab';
import { TrackingProvider } from 'api/tracking/enums/TrackingProvider';
import CarAlertsTab from './components/carAlertsTab/CarAlertsTab';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import CarGlobalChargeTab from './components/carMonthlyChargesTab/CarMonthlyChargesTab';
import CalendarIcon from 'assets/svg/calendar.svg';
import GaugeIcon from 'assets/svg/gauge.svg';
import ClockIcon from 'assets/svg/desktop_i_clock.svg';
import DropIcon from 'assets/svg/drop.svg';
import TicketIcon from 'assets/svg/ticket.svg';
import FloorBrushIcon from 'assets/svg/floor-brush.svg';
import MediaIcon from 'assets/svg/Grupo_352.svg';
import UserIcon from 'assets/svg/desktop_default_photo.svg';
import ReserveIcon from 'assets/svg/calendar_v_routine.svg';
import IconWithText from 'common/components/iconWithText/IconWithText';
import Label from 'common/components/label/Label';
import dayjs from 'dayjs';
import AlertsCounter from 'common/components/alertsCounter/AlertsCounter';
import HelperModal from 'common/components/helperModal/HelperModal';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import InfoIcon from 'assets/svg/i_info_primaryDark.svg';
import InfoTour from 'common/components/info-tour/InfoTour';
import { VehicleConsumptionsResultDto } from 'api/vehicles/models/VehicleDto';
import ReactTooltip from 'react-tooltip';
import { FiltersFormModel } from 'screens/dashboard/filters/FiltersView';
import CarReminderTab from './components/carReminderTab/CarReminderTab';
import IconBack from 'assets/svg/Arrow-grey.svg';
import DeletedVehicleCircle from 'common/components/deletedVehicleCircle/DeletedVehicleCircle';
import VatNotice from 'common/components/vatNotice/VatNotice';

export type Types = 'edit' | 'details' | 'new' | 'list';

const VehicleScreen = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { vehicleId, type, tabId, itemType, returnUrl } = useParams<{ vehicleId: string, type: Types, tabId: string, itemType: string, itemId: string, returnUrl: string }>();
    const { addToast } = useToasts();
    const imageCacheKey = useRef(new Date().getTime());

    const [tabAux, setTabAux] = useState<string | undefined>(undefined);
    const [item, setItem] = useState<VehicleViewModel>();
    const [itemOriginal, setItemOriginal] = useState<VehicleViewModel>();
    const [consumationTotals, setConsumationTotals] = useState<VehicleConsumptionsResultDto | undefined>(undefined);
    const [averageEfficiencyTooltip, setAverageEfficiencyTooltip] = useState<string | undefined>(undefined);
    const [averageCostTooltip, setAverageCostTooltip] = useState<string | undefined>(undefined);

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasVehiclesWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_VEHICLES_WRITE']);
    const hasVehiclesReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_VEHICLES_READ']);
    const hasContractsReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_CONTRACTS_READ']);
    const hasChargesReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_CHARGES_READ']);
    const hasVehicleMaintenancesReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_VEHICLE_MAINTENANCES_READ']);
    const hasAlertsReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_ALERTS_READ']);
    const hasVerificationsReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_VERIFICATIONS_READ']);
    const hasAccidentsReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_ACCIDENTS_READ']);
    const hasGpsKmsPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_GPS_READ']);
    const hasCountersPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['CHARGES_TOTAL_READ']);
    const hasDashboardPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_DASHBOARDS_READ']);
    const hasRemindersReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_REMINDER_READ']);

    const [tourOpen, setTourOpen] = useState<boolean>(false);
    const steps = [
        {
            selector: '#first-step',
            content: <HelperModal title={t('vehicles.list.tour.details')} text={t('vehicles.list.tour.details_message')} />,
            tab: ''
        }
    ];

    const tabs: TabItem[] = [];
    const [tourReRender, setUpdate] = useState<string>('0');
    const manageTourReRender = () => {
        setUpdate((parseInt(tourReRender) + 1).toString());
    }

    const init = () => {

        if (hasDashboardPolicy) {
            tabs.push({
                id: 'dashboard',
                title: t('vehicle.dashboard'),
                content: <CarDashboardTab tourReRender={manageTourReRender} hasTrackingDeviceId={Boolean(item?.trackingDeviceId)} reservationAvailable={Boolean(item?.reservationAvailable)} hasContractedKm={Boolean(item?.hasContractedKm)} vehicleFuels={item?.vehicleFuels ?? []} segmentFeatures={item?.segmentFeatures ?? []} registrationNumber={item?.registrationNumber} hasFuelLevel={Boolean(item?.hasFuelLevel)} onMonthlyCostDashboardClick={onMonthlyCostDashboardClick} />
            });
            steps.push({
                selector: '#general-step',
                content: <HelperModal title={t('vehicles.list.tour.dashboard')} text={t('vehicles.list.tour.dashboard_message')} />,
                tab: 'dashboard'
            });
        }

        if (hasVehiclesWritePolicy || hasVehiclesReadPolicy) {
            tabs.push({
                id: 'informations',
                title: t('vehicle.car_document'),
                content: <CarDocumentTab mode={type} item={item} onCancel={onClose} onVehicleUpdated={getData} itemOriginal={itemOriginal} setItem={setItem} />
            });
            steps.push({
                selector: '#general-step',
                content: <HelperModal title={t('vehicles.list.tour.car_document')} text={t('vehicles.list.tour.car_document_message')} />,
                tab: 'informations'
            });
        }

        if (hasContractsReadPolicy) {
            tabs.push({
                id: 'contract',
                title: t('vehicle.contract'),
                content: <CarContractTab vehicle={item} onDeleteFromList={onDeleteFromList} tourReRender={manageTourReRender} />
            });
            steps.push({
                selector: '#general-step',
                content: <HelperModal title={t('vehicles.list.tour.contract')} text={t('vehicles.list.tour.contract_message')} />,
                tab: 'contract'
            });

            // steps.push({
            //     selector: '#contracts-1',
            //     content: <HelperModal title={'Contratos de Uso'} text='Contratos relacionados com o aluger do veículo.' />,
            //     tab: 'contract'
            // });

            // steps.push({
            //     selector: '#contracts-2',
            //     content: <HelperModal title={'Contratos de Manutenção'} text='Contratos relacionados com a manutenção do veículo ' />,
            //     tab: 'contract'
            // });

            // steps.push({
            //     selector: '#contracts-3',
            //     content: <HelperModal title={'Contratos de Seguro'} text='Lista de contratos de seguro associados ao veiculo' />,
            //     tab: 'contract'
            // });
        }

        if (hasChargesReadPolicy) {
            tabs.push({
                id: 'costs',
                title: t('vehicle.costs'),
                content: <CarChargeTab vehicle={item} onDeleteFromList={onDeleteFromList} tourReRender={manageTourReRender} />
            });
            steps.push({
                selector: '#general-step',
                content: <HelperModal title={t('vehicles.list.tour.costs')} text={t('vehicles.list.tour.costs_message')} />,
                tab: 'costs'
            });
        }

        if (hasVehicleMaintenancesReadPolicy) {
            tabs.push({
                id: 'maintenance',
                title: t('vehicle.maintenance'),
                content: <CarMaintenanceTab vehicle={item} onDeleteFromList={onDeleteFromList} tourReRender={manageTourReRender} />
            });
            steps.push({
                selector: '#general-step',
                content: <HelperModal title={t('vehicles.list.tour.maintenance')} text={t('vehicles.list.tour.maintenance_message')} />,
                tab: 'maintenance'
            });
        }

        if (hasAlertsReadPolicy) {
            tabs.push({
                id: 'alerts',
                title: <>
                    <span className={styles.itemText}>
                        {t('vehicle.alerts')}
                        {(item && (item.totalAlerts ?? 0) > 0) && <AlertsCounter totalAlerts={(item.totalAlerts ?? 0)} className={styles.floatingBadge} size='small' />}
                    </span>
                </>,
                content: <CarAlertsTab vehicle={item} tourReRender={manageTourReRender} />
            });
            steps.push({
                selector: '#general-step',
                content: <HelperModal title={t('vehicles.list.tour.alerts')} text={t('vehicles.list.tour.alerts_message')} />,
                tab: 'alerts'
            });
        }

        if (hasGpsKmsPolicy && item?.companyTrackingProvider && item?.companyTrackingProvider !== TrackingProvider.NONE && item.trackingDeviceId) {
            tabs.push({
                id: 'gps',
                title: t('vehicle.gps'),
                content: <CarGpsTab vehicleId={item?.id || ''} tourReRender={manageTourReRender} />
            });
            steps.push({
                selector: '#general-step',
                content: <HelperModal title={t('vehicles.list.tour.gps')} text={t('vehicles.list.tour.gps_message')} />,
                tab: 'gps'
            });
        }

        if (hasVerificationsReadPolicy) {
            tabs.push({
                id: 'verifications',
                title: t('vehicle.verifications'),
                content: <CarVerificationTab vehicle={item} tourReRender={manageTourReRender} onVehicleReplace={(value: boolean) => { setItem(prev => ({ ...prev!, replacedVehicle: value })); }} onVerificationsUpdated={getData} />
            });
            steps.push({
                selector: '#general-step',
                content: <HelperModal title={t('vehicles.list.tour.verifications')} text={t('vehicles.list.tour.verifications_message')} />,
                tab: 'verifications'
            });
        }

        if (hasAccidentsReadPolicy) {
            tabs.push({
                id: 'accidents',
                title: t('vehicle.accidents'),
                content: <CarAccidentTab vehicle={item} tourReRender={manageTourReRender} />
            });
            steps.push({
                selector: '#general-step',
                content: <HelperModal title={t('vehicles.list.tour.accidents')} text={t('vehicles.list.tour.accidents_message')} />,
                tab: 'accidents'
            });
        }

        if (hasChargesReadPolicy && hasVehicleMaintenancesReadPolicy && hasContractsReadPolicy && hasCountersPolicy) {
            tabs.push({
                id: 'monthlyCharges',
                title: t('vehicle.monthly_costs'),
                content: <CarGlobalChargeTab tourReRender={manageTourReRender} />
            });
            steps.push({
                selector: '#general-step',
                content: <HelperModal title={t('vehicles.list.tour.monthly_charges')} text={t('vehicles.list.tour.monthly_charges_message')} />,
                tab: 'monthlyCharges'
            });
        }


        if (hasRemindersReadPolicy) {
            tabs.push({
                id: 'reminders',
                title: t('vehicle.reminders'),
                content: <CarReminderTab vehicle={item} onDeleteFromList={onDeleteFromList} tourReRender={manageTourReRender} />
            });
            steps.push({
                selector: '#general-step',
                content: <HelperModal title={t('vehicles.list.tour.reminders')} text={t('vehicles.list.tour.reminders_message')} />,
                tab: 'reminders'
            });
        }


    }

    const onMonthlyCostDashboardClick = (filter: FiltersFormModel, startDate?: Date) => {
        let dateToString = '';
        let dateFromString = '';

        if (startDate) {
            const dateFrom = new Date(startDate);
            dateFrom.setHours(0, 0, 0, 0);
            dateFromString = dateFrom.toString();
            const dateTo = new Date(dayjs(startDate).endOf('month').toDate());
            dateTo.setHours(0, 0, 0, 0);
            dateToString = dateTo.toString();
        }
        else {
            const dateTo = new Date(filter.dateTo!);
            dateTo.setHours(0, 0, 0, 0);
            dateToString = dateTo.toString();
            const dateFrom = new Date(dayjs(filter.dateFrom!).startOf('month').toDate());
            dateFrom.setHours(0, 0, 0, 0);
            dateFromString = dateFrom.toString();
        }

        localStorage.setItem('vehicle-monthly-charges-filters', JSON.stringify({
            itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
            page: 1,
            vehicleId: vehicleId,
            orderColumn: 'vmc.date',
            orderBy: 'desc',
            endDate: new Date(dateToString),
            startDate: new Date(dateFromString)
        }));

        setTabAux(undefined);
        history.push(`/vehicles/details/${vehicleId}/monthlyCharges/list`);

    }

    const getData = async () => {
        try {
            if (vehicleId) {
                Loading.show();
                const result = await VehiclesService.getById(vehicleId);
                if (!!result) {
                    if (result.registrationNumberDate) {
                        result.registrationNumberDate = new Date(result.registrationNumberDate);
                    }
                    const resultConsumation = await VehiclesService.getConsumptionCounters({
                        itemsPerPage: 0, page: 0,
                        vehicleId
                    });
                    setConsumationTotals(resultConsumation);
                    if (resultConsumation) {
                        if (resultConsumation.vehicleConsumptionsByFuel.length > 1) {
                            let aux = '';
                            resultConsumation.vehicleConsumptionsByFuel.forEach(x => {
                                aux += (aux == '' ? '' : ' + ');
                                aux += `${x.averageEfficiency.toFixed(2).replace('.', ',')} ${t((x.fuelName == FUELS.ELECTRIC ? 'vehicle.average_efficienc_eletric_unity' : 'vehicle.average_efficienc_unity') as any)}`;
                            });
                            setAverageEfficiencyTooltip(aux);
                        } else {
                            setAverageEfficiencyTooltip(undefined);
                        }

                        setAverageCostTooltip(`${resultConsumation.totalCost.toFixed(2).replace('.', ',')} € / ${resultConsumation.totalKilometers.toFixed(2).replace('.', ',')} kms = ${resultConsumation.averageCost.toFixed(2).replace('.', ',')} ${t('vehicle.costs_consumption_unit')}`);
                    }
                }

                setItem(result);
                setItemOriginal(result);
                Loading.hide();
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get vehicle information `, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    }

    const canAddVehicles = async () => {
        try {
            Loading.show();
            await VehiclesService.candAddVehicles();
            Loading.hide();
        } catch (error) {
            if (error?.response?.status === 402) {
                addToast(t('common.errors.pack_limit_exceeded'), { appearance: 'warning' });
                history.push(`/account/details/subscription`);
            }
        }
    }

    const navigateToTab = (id: string) => {
        setTabAux(undefined);
        history.replace(`/vehicles/details/${vehicleId}/${id}/list`);
    }

    const onClose = (result?: Types) => {
        if (vehicleId) {
            if (result === 'edit') {
                history.push(`/vehicles/details/${vehicleId}`);
            }
        }
    };

    const onDeleteFromList = () => {
        getData();
    }

    const showField = (feature: string) => {
        return (item?.segmentFeatures?.findIndex(x => x == feature) ?? -1) >= 0;
    }

    const goBack = () => {
        if (history.length > 1) history.goBack();
        else history.push('/dashboard');
    }

    useEffect(() => {
        if (!vehicleId) {
            canAddVehicles();
        }
        getData();
    }, [vehicleId, tabId, itemType, type]);

    init();

    return (
        <ScreenTitle title={t('vehicles.title')}>
            {type == 'new' ? (
                <CompactPageLayout
                    title={t('vehicle.new') as string}
                    onBack={() => history.goBack()}
                >
                    <CarForm onCancel={onClose} />
                </CompactPageLayout>
            ) : (
                <ScreenContainer className={styles.pageContainer}>
                    {itemOriginal &&
                        <>
                        <Box className={`${styles.headerBox} ${itemOriginal && (itemOriginal.immobilized || !itemOriginal.active) ? styles.opacity : ''} ${item && item.immobilized ? styles.opacityBackground : ''} ${item && item.replacedVehicle ? styles.replacedVehicleBorder : ''}`} id="first-step">
                            {itemOriginal.replacedVehicle ? (
                                <div className={`${styles.replacementBanner} ${styles.replacedBannerColor}`}>
                                    {t('replacement_vehicles.replaced_by')} {itemOriginal.replacementVehicle}
                                </div>
                            )
                                :
                                itemOriginal.isReplacementVehicle &&
                                (<div className={`${styles.replacementBanner} ${styles.replacementBannerColor}`}>
                                    {t('replacement_vehicles.in_replacement_of')} {itemOriginal.replacingVehicle}
                                </div>)
                            }

                            {!itemOriginal.active && !itemOriginal.replacedVehicle &&  !itemOriginal.isReplacementVehicle && (
                                <div className={`${styles.replacementBanner} ${styles.inactiveBannerColor}`}>
                                    <div className={styles.inactiveText}> {t('vehicle.not_active')}</div>
                                </div>
                            )}

                            {steps.length > 0 && <Row className={styles.header}>
                                <Col xs={6} style={{ textAlign: 'left' }}><ScreenHeaderButton className={styles.iconBack} icon={IconBack} onClick={goBack} /></Col>
                                <Col xs={6} style={{ textAlign: 'right' }}><ScreenHeaderButton icon={InfoIcon} onClick={() => setTourOpen(true)} /></Col>
                            </Row>}
                            <Row>
                                <Col xs={12} md={6} lg={4} className={styles.col} style={{ paddingLeft: 0 }}>
                                    <div className={styles.divDetailsCar}>
                                        <div className={styles.divImage}>
                                            {itemOriginal && <div className={styles.img} style={{ backgroundImage: itemOriginal?.photoUrl ? 'url(' + itemOriginal?.photoUrl + '?_=' + imageCacheKey.current + ')' : itemOriginal?.vehicleSegmentDefaultPhotoUrl ? 'url(' + itemOriginal?.vehicleSegmentDefaultPhotoUrl + '?_=' + imageCacheKey.current + ')' : 'url(' + DefaultAssetImage + ')' }} />}
                                            {itemOriginal.immobilized && <div className={styles.overlay}>
                                                <div className={styles.overlayText}>{t('vehicle.immobilized')}</div>
                                            </div>}
                                        </div>

                                        <div className={styles.divDesc}>
                                            <div className={`${styles.content}`}>
                                                <div className={styles.labelRegistration}>{itemOriginal?.registrationNumber} {!itemOriginal.active && <DeletedVehicleCircle id={itemOriginal.id ?? ''} />}</div>
                                                <div className={`${styles.labelDetails} ${itemOriginal && !itemOriginal.active && !itemOriginal.immobilized ? styles.labelDetailsColor : ''} ${itemOriginal && !itemOriginal.active ? styles.colorTextWarning : ''}`}>{(itemOriginal?.vehicleBrandName ?? '') + (' ') + (itemOriginal?.vehicleModelName ?? '')}</div>
                                                <div className={`${styles.labelDetails} ${itemOriginal && !itemOriginal.active && !itemOriginal.immobilized ? styles.labelDetailsColor : ''} ${itemOriginal && !itemOriginal.active ? styles.colorTextWarning : ''}`}>{itemOriginal?.vehicleSegmentName + ((itemOriginal.vehicleFuels?.length || itemOriginal.colorName) ? ' | ' : '')}
                                                    {(itemOriginal.vehicleFuels?.map((x, i) => (
                                                        <span key={'fuel' + i}> {t(('vehicle.fuels.' + x.fuelName) as any).toString()} |  </span>
                                                    )))}
                                                    {itemOriginal?.colorName ? (t(('vehicle.colors.' + itemOriginal?.colorName) as any)) : ''}</div>
                                                {Boolean(itemOriginal?.vehicleLocals?.length) && <div className={`${styles.labelLocal} ${itemOriginal && !itemOriginal.active && !itemOriginal.immobilized ? styles.labelDetailsColor : ''}`}>{itemOriginal?.vehicleLocals?.map(x => x.localName).join(', ')}</div>}

                                            </div>
                                        </div>
                                    </div>
                                </Col>
                                <Col xs={12} md={6} lg={4} className={`${styles.col} ${styles.borderLeft}`}>
                                    <div className={`${styles.cont}`}>
                                        <div className={`${styles.content}`}>

                                            <div className={styles.info}>
                                                <div className={styles.label}>
                                                    <IconWithText
                                                        icon={UserIcon}
                                                        value={t('vehicle.driver')}
                                                        size={'sm'}
                                                    />
                                                </div>
                                                <div className={styles.text}>
                                                    <Label style={{ fontWeight: 'bold' }}>{itemOriginal && itemOriginal.driverName ? itemOriginal.driverName : '-'}</Label>
                                                </div>
                                            </div>

                                            <div className={styles.info}>
                                                <div className={styles.label}>
                                                    <IconWithText
                                                        icon={ReserveIcon}
                                                        value={t('vehicle.next_reserve')}
                                                        size={'sm'}
                                                    />
                                                </div>
                                                <div className={styles.text} data-tip={itemOriginal && itemOriginal.reservationAvailable && itemOriginal.reservationDriverName ? t('vehicle.driver') + ': ' + itemOriginal.reservationDriverName : ''} data-for='reservationDate'>
                                                    {(itemOriginal && itemOriginal.reservationAvailable && itemOriginal.reservationStartDate && itemOriginal.reservationEndDate)
                                                        ? `${dayjs(itemOriginal.reservationStartDate).format(DATE_FORMAT_DEFAUT)} — ${dayjs(itemOriginal.reservationEndDate).format(DATE_FORMAT_DEFAUT)}`
                                                        : '-'
                                                    }
                                                </div>
                                            </div>

                                            {showField(SEGMENT_FEATURES.KM) && <div className={styles.info}>
                                                <div className={styles.label}>
                                                    <IconWithText
                                                        icon={CalendarIcon}
                                                        value={t('vehicle.registration')}
                                                        size={'sm'}
                                                    />
                                                </div>
                                                <div className={styles.text}>{itemOriginal?.registrationNumberDate ? new Date(itemOriginal.registrationNumberDate).toLocaleDateString([], { year: 'numeric', month: '2-digit' }) : ''}</div>
                                            </div>}


                                            {showField(SEGMENT_FEATURES.KM) && <div className={styles.info}>
                                                <div className={styles.label}>
                                                    <IconWithText
                                                        icon={GaugeIcon}
                                                        value={t('vehicle.current_kilometers')}
                                                        size={'sm'}
                                                    />
                                                </div>
                                                <div className={styles.text}><MoneyFormat value={itemOriginal && itemOriginal.currentKilometers ? itemOriginal.currentKilometers : '-'} suffix={t('common.kms')} fixedDecimalScale={false} /></div>
                                            </div>}

                                            {showField(SEGMENT_FEATURES.HOURS) && <div className={styles.info}>
                                                <div className={styles.label}>
                                                    <IconWithText
                                                        icon={ClockIcon}
                                                        value={t('vehicle.current_hours') as string}
                                                        size={'sm'}
                                                    />
                                                </div>
                                                <div className={styles.text}><MoneyFormat value={itemOriginal && itemOriginal.currentHours ? itemOriginal.currentHours : '-'} suffix={t('common.hours_symbol') as string} fixedDecimalScale={false} /></div>
                                            </div>}

                                        </div>
                                    </div>
                                </Col>
                                <Col xs={12} md={6} lg={4} className={`${styles.col} ${styles.borderLeft}`}>

                                    <div className={`${styles.cont}`}>
                                        <div className={`${styles.content}`}>

                                            {showField(SEGMENT_FEATURES.FUEL) && !!consumationTotals &&
                                                consumationTotals.vehicleConsumptionsByFuel.length == 1 &&
                                                consumationTotals.vehicleConsumptionsByFuel.map((x, i) => (
                                                    <div className={styles.info} key={'fueltotal_' + i}>
                                                        <div className={styles.label}>
                                                            <IconWithText
                                                                icon={DropIcon}
                                                                value={t('vehicle.average_consumption') + ' ' + t(('vehicle.fuels.' + x.fuelName) as any)}
                                                                size={'sm'}
                                                            />
                                                        </div>
                                                        <div className={`${styles.text} ${x.consumption != undefined && styles.divPoint}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
                                                            data-for='global'
                                                            data-tip={x.consumption != undefined ? (x.consumption > (x?.consommationMoyenneReference ?? 0) ? t('vehicle.consommation_moyenne_negative', { current_vehicle_consumption: x.consumption != undefined ? x.consumption.toFixed(2).replace('.', ',') + ' ' + t((x.fuelName == FUELS.ELECTRIC ? 'vehicle.eletric_consumption_unit' : 'vehicle.average_consumption_unit') as any) : '-', reference_vehicle_consumption: (x?.consommationMoyenneReference.toFixed(2).replace('.', ',') ?? '-') + ' ' + t((x.fuelName == FUELS.ELECTRIC ? 'vehicle.eletric_consumption_unit' : 'vehicle.average_consumption_unit') as any) }) : t('vehicle.consommation_moyenne_positive', { current_vehicle_consumption: x.consumption != undefined ? x.consumption.toFixed(2).replace('.', ',') + ' ' + t((x.fuelName == FUELS.ELECTRIC ? 'vehicle.eletric_consumption_unit' : 'vehicle.average_consumption_unit') as any) : '-', reference_vehicle_consumption: (x?.consommationMoyenneReference.toFixed(2).replace('.', ',') ?? '-') + ' ' + t((x.fuelName == FUELS.ELECTRIC ? 'vehicle.eletric_consumption_unit' : 'vehicle.average_consumption_unit') as any) })) : ''}
                                                        >
                                                            {x.consumption != undefined && <MoneyFormat value={x.consumption} suffix={t((x.fuelName == FUELS.ELECTRIC ? 'vehicle.eletric_consumption_unit' : 'vehicle.average_consumption_unit') as any)} fixedDecimalScale={false} decimalScale={2} />}
                                                            {x.consumption != undefined && <div className={`${styles.point} ${x.consumption > (x?.consommationMoyenneReference ?? 0) ? styles.pointRed : styles.pointGreen}`} />}
                                                            {x.consumption == undefined && '-'}
                                                        </div>

                                                    </div>
                                                ))}

                                            {showField(SEGMENT_FEATURES.FUEL) && !!consumationTotals &&
                                                consumationTotals.vehicleConsumptionsByFuel.length > 1 &&
                                                <div className={styles.info}>
                                                    <div className={styles.label}>
                                                        <IconWithText
                                                            icon={DropIcon}
                                                            value={t('vehicle.average_efficiency')}
                                                            size={'sm'}
                                                        />
                                                    </div>
                                                    <div className={`${styles.text} ${consumationTotals.averageEfficiency != undefined && styles.divPoint}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
                                                        data-tip={averageEfficiencyTooltip} data-for='global3'
                                                    >
                                                        {consumationTotals.averageEfficiency != undefined && <MoneyFormat value={consumationTotals.averageEfficiency} prefix={'≈ '} suffix={t('vehicle.average_efficienc_unity')} fixedDecimalScale={false} decimalScale={2} />}
                                                        {consumationTotals.averageEfficiency == undefined && '-'}
                                                    </div>

                                                </div>
                                            }

                                            {showField(SEGMENT_FEATURES.FUEL) && !!consumationTotals && <div className={styles.info}>
                                                <div className={styles.label}>
                                                    <IconWithText
                                                        icon={DropIcon}
                                                        value={t('vehicle.average_cost')}
                                                        size={'sm'}
                                                    />
                                                </div>
                                                <div className={`${styles.text} ${consumationTotals.averageCost != undefined && styles.divPoint}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
                                                    data-tip={averageCostTooltip} data-for='global2'
                                                >
                                                    {consumationTotals.averageCost != undefined && <MoneyFormat value={consumationTotals.averageCost} suffix={t('vehicle.costs_consumption_unit')} fixedDecimalScale={false} decimalScale={2} />}
                                                    {consumationTotals.averageCost == undefined && '-'}
                                                </div>

                                            </div>}

                                            <ReactTooltip id='global' />
                                            <ReactTooltip id='global2' />
                                            <ReactTooltip id='global3' />
                                            <ReactTooltip id='reservationDate' />
                                            <ReactTooltip id='replacementVehicle' />
                                            <div className={styles.info}>
                                                <div className={styles.label}>
                                                    <IconWithText
                                                        icon={TicketIcon}
                                                        value={t('vehicle.cost')}
                                                        size={'sm'}
                                                    />
                                                </div>
                                                <div className={styles.text}><MoneyFormat fixedDecimalScale={true} value={itemOriginal?.fines ?? '-'} suffix={t('common.euro')} /></div>
                                            </div>

                                            <div className={styles.info}>
                                                <div className={styles.label}>
                                                    <IconWithText
                                                        icon={FloorBrushIcon}
                                                        value={t('vehicle.last_cleaning')}
                                                        size={'sm'}
                                                    />
                                                </div>
                                                <div className={styles.text}>{itemOriginal?.lastCleaning ? <DateFormat value={itemOriginal.lastCleaning} /> : '-'}</div>
                                            </div>

                                            {hasCountersPolicy && <div className={styles.info} style={{ cursor: 'pointer' }} data-tip={t('vehicle.average_monthly_cost_tooltip')} data-for='averageMonthlyCost' onClick={() => {
                                                localStorage.setItem('vehicle-monthly-charges-filters', JSON.stringify({ endDate: dayjs().endOf('month').toDate() }));
                                                window.dispatchEvent(new CustomEvent('reset-monthly-charges-filters'));
                                                navigateToTab('monthlyCharges');
                                            }}>
                                                <div className={styles.label}>
                                                    <IconWithText
                                                        icon={MediaIcon}
                                                        value={t('vehicle.average_monthly_cost')}
                                                        size={'sm'}
                                                    />
                                                </div>
                                                <div className={styles.text}>{itemOriginal?.monthlyCostAverage ? <MoneyFormat fixedDecimalScale={true} value={itemOriginal?.monthlyCostAverage ?? '-'} suffix={t('common.euro')} /> : '-'}</div>
                                                <ReactTooltip id='averageMonthlyCost' />
                                            </div>}

                                        </div>
                                    </div>

                                </Col>
                            </Row>
                            <VatNotice className={styles.vatNotice} />
                        </Box>


                        <div id="general-step" className={styles.tabsWrapper}>
                            <Tabs
                                items={tabs}
                                activeTabId={tabAux ?? tabId ?? 'informations'}
                                onChange={navigateToTab}
                                emptyTabMessage={t('vehicle.message_no_permission')}
                            />
                        </div>
                    </>
                }
            </ScreenContainer>
            )}
            <InfoTour
                steps={steps}
                isOpen={tourOpen}
                onRequestClose={() => setTourOpen(false)}
                changeTab={setTabAux}
                startAt={(tabAux ?? tabId ?? 'informations') != 'informations' ? steps.findIndex(x => x.tab == (tabAux ?? tabId ?? 'informations')) : 0}
                reRender={tourReRender}
                className={styles.tour}
            />

        </ScreenTitle>
    );
}

export default VehicleScreen;